import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useApolloClient } from '@apollo/client';
import { useMutationWithToast } from '@pesto/ui';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { z } from 'zod';

import type { Program, UpdateProgramInput } from '../../../__generated__/graphql/api';
import type { ModalProps } from '../../types/modal.types';

import {
  GraceStrategy,
  LateFeeStrategy,
  MinimumPaymentStrategy,
  PdfTemplate,
  ReturnedPaymentFeeStrategy,
  useProgramQuery,
  useSetProgramMerchantPolicyMutation,
  useUpdateProgramMutation,
} from '../../../__generated__/graphql/api';
import { getProgramMutationRefreshOptions } from '../../../utils/programCacheInvalidation';
import { PDF_TEMPLATES_BY_POLICY, toPdfTemplateOptions } from '../../../utils/programPdfTemplates';
import { createFormComponent } from '../../utils/create-form-component';
import { YES_NO_OPTIONS } from '../../utils/form-helpers';
import {
  billingDaySchema,
  optionalUuidSchema,
  PERCENT_PATTERN,
  percentFieldSchema,
  percentStringToRate,
} from './programFormSchemas';

const creditCardStrategyCondition = {
  when: {
    field: 'minimumPaymentStrategy' as const,
    operator: 'equals' as const,
    value: MinimumPaymentStrategy.CreditCardPercentOfBalance,
  },
};

const minimumPaymentStrategyOptions = [
  {
    value: MinimumPaymentStrategy.HelocCumulative,
    label: 'HELOC (cumulative interest + fees)',
  },
  {
    value: MinimumPaymentStrategy.CreditCardPercentOfBalance,
    label: 'Credit card (% of balance)',
  },
];

const lateFeeStrategyOptions = [
  {
    value: LateFeeStrategy.PercentOfMinimum,
    label: 'Percent of minimum payment',
  },
  {
    value: LateFeeStrategy.FlatTiered,
    label: 'Flat first/subsequent amounts',
  },
];

const graceStrategyOptions = [
  {
    value: GraceStrategy.Retroactive,
    label: 'Retroactive — deferred interest posts at the due date',
  },
  {
    value: GraceStrategy.Waive,
    label: 'Waive — unpaid portion only (Reg Z §1026.54)',
  },
];

const returnedPaymentFeeStrategyOptions = [
  {
    value: ReturnedPaymentFeeStrategy.Flat,
    label: 'Flat — the fee amount charged as-is',
  },
  {
    value: ReturnedPaymentFeeStrategy.Capped,
    label: 'Capped — min(fee, minimum due) (Reg Z §1026.52(b)(2)(i))',
  },
];

const decimalRateToPercentString = (rate: string | null | undefined): string => {
  if (rate === null || rate === undefined || rate === '') return '';
  const parsed = parseFloat(rate);

  if (Number.isNaN(parsed)) return '';
  const percent = parsed * 100;

  return Number.isInteger(percent) ? percent.toString() : percent.toFixed(4).replace(/\.?0+$/, '');
};

const updateProgramSchema = (hasBillingDefaults: boolean) =>
  z
    .object({
      program_id: z.string().min(1, 'Program ID is required'),
      twispJournalId: optionalUuidSchema('Twisp Journal ID'),
      twispIsoConfigId: optionalUuidSchema('Twisp ISO Config ID'),
      twispExposureAccountSetId: optionalUuidSchema('Twisp Exposure Account Set ID'),
      name: z.string().min(1, 'Name is required'),
      targetInterestRate: z
        .string()
        .min(1, 'Interest rate is required')
        .regex(/^\d+(\.\d+)?$/, 'Interest rate must be a valid number')
        .refine((val) => parseFloat(val) >= 0 && parseFloat(val) <= 100, 'Interest rate must be between 0 and 100'),
      minApr: z
        .string()
        .min(1, 'Min APR is required')
        .regex(/^\d+(\.\d+)?$/, 'Min APR must be a valid number')
        .refine((val) => parseFloat(val) >= 0 && parseFloat(val) <= 100, 'Min APR must be between 0 and 100'),
      maxApr: z
        .string()
        .min(1, 'Max APR is required')
        .regex(/^\d+(\.\d+)?$/, 'Max APR must be a valid number')
        .refine((val) => parseFloat(val) >= 0 && parseFloat(val) <= 100, 'Max APR must be between 0 and 100'),
      firstLateFeeAmount: z
        .number()
        .min(1, 'Late fee amount is required')
        .refine((val) => val >= 0, 'Late fee amount must be positive'),
      subsequentLateFeeAmount: z
        .number()
        .refine((val) => val >= 0, 'Subsequent late fee amount must be positive')
        .optional(),
      lateFeeStrategy: z.nativeEnum(LateFeeStrategy),
      returnedPaymentFeeCents: z.number().refine((val) => val >= 0, 'Returned payment fee must be positive'),
      returnedPaymentFeeStrategy: z.nativeEnum(ReturnedPaymentFeeStrategy).optional(),
      foreignTransactionFeeRatePercent: percentFieldSchema('Foreign transaction fee rate'),
      cashAdvancesAllowed: z.boolean(),
      cashAdvanceFeeRatePercent: z.string().optional(),
      cashAdvanceFeeMinCents: z.number().optional(),
      replacementCardFeeCents: z.number().refine((val) => val >= 0, 'Replacement card fee must be positive'),
      paymentDueDateGracePeriodDays: z
        .string()
        .min(1, 'Minimum close-to-due days is required')
        .regex(/^\d+$/, 'Minimum close-to-due days must be a whole number')
        .refine(
          (val) => parseInt(val) >= 0 && parseInt(val) <= 28,
          'Minimum close-to-due days must be between 0 and 28',
        )
        .or(z.literal(''))
        .refine((value) => value !== '' || !hasBillingDefaults, 'Minimum close-to-due days is required'),
      lateFeeGracePeriodDays: z
        .string()
        .min(1, 'Late fee grace period days is required')
        .regex(/^\d+$/, 'Late fee grace period days must be a whole number')
        .refine(
          (val) => parseInt(val) >= 0 && parseInt(val) <= 28,
          'Late fee grace period days must be between 0 and 28',
        ),
      statementGenerateDay: hasBillingDefaults ? billingDaySchema('Statement generation day') : z.literal(''),
      paymentDueDay: hasBillingDefaults ? billingDaySchema('Payment due day') : z.literal(''),
      minimumPaymentStrategy: z.nativeEnum(MinimumPaymentStrategy),
      minimumPaymentRatePercent: z.string().optional(),
      minimumPaymentFloorCents: z.number().optional(),
      graceStrategy: z.nativeEnum(GraceStrategy).optional(),
      pdfTemplate: z.nativeEnum(PdfTemplate).optional(),
      chargeDrawdownFee: z.enum(['true', 'false']),
    })
    .refine((data) => parseFloat(data.minApr) <= parseFloat(data.maxApr), {
      message: 'Max APR must be greater than or equal to Min APR',
      path: ['maxApr'],
    })
    .superRefine((data, ctx) => {
      if (data.statementGenerateDay && Number(data.statementGenerateDay) === Number(data.paymentDueDay)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['paymentDueDay'],
          message: 'Payment due day must differ from statement generation day.',
        });
      }

      if (data.lateFeeStrategy === LateFeeStrategy.FlatTiered && data.subsequentLateFeeAmount === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['subsequentLateFeeAmount'],
          message: 'Subsequent late fee amount is required for flat tiered fees',
        });
      }

      if (data.minimumPaymentStrategy !== MinimumPaymentStrategy.CreditCardPercentOfBalance) return;

      const rawRate = data.minimumPaymentRatePercent ?? '';

      if (!rawRate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minimumPaymentRatePercent'],
          message: 'Minimum payment rate is required',
        });
      } else if (!PERCENT_PATTERN.test(rawRate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minimumPaymentRatePercent'],
          message: 'Minimum payment rate must be a percent with at most two decimals',
        });
      } else {
        const parsed = parseFloat(rawRate);

        if (parsed < 0 || parsed > 100) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['minimumPaymentRatePercent'],
            message: 'Minimum payment rate must be between 0 and 100',
          });
        }
      }

      if (data.minimumPaymentFloorCents === undefined || data.minimumPaymentFloorCents === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minimumPaymentFloorCents'],
          message: 'Minimum payment floor is required',
        });
      } else if (data.minimumPaymentFloorCents < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minimumPaymentFloorCents'],
          message: 'Minimum payment floor must be greater than or equal to 0',
        });
      }
    });

type UpdateProgramFormData = z.infer<ReturnType<typeof updateProgramSchema>>;

export const EditProgramComponent = ({
  payload,
  api,
}: ModalProps<{ programId: string; programData?: Program }, true>) => {
  const { programId } = payload;
  const { handleMutation } = useMutationWithToast();
  const client = useApolloClient();

  const { data: programData } = useProgramQuery({
    variables: { programId },
    skip: !!payload.programData,
  });

  const program = payload.programData ?? programData?.program;

  const hasBillingDefaults = program?.statementGenerateDay != null && program?.paymentDueDay != null;

  const [updateProgram, { loading }] = useUpdateProgramMutation(getProgramMutationRefreshOptions());

  const [setProgramMerchantPolicy, { loading: policyLoading }] = useSetProgramMerchantPolicyMutation(
    getProgramMutationRefreshOptions(),
  );

  const currentCashAdvancesAllowed = program?.cashAdvancesAllowed ?? false;

  const saveCashAdvances = (cashAdvancesAllowed: boolean) =>
    handleMutation({
      mutation: () => setProgramMerchantPolicy({ variables: { programId, cashAdvancesAllowed } }),
      extractSuccess: (result) => !!result?.data?.setProgramMerchantPolicy,
      debugLabel: 'set-program-merchant-policy',
      toast: {
        success: 'Cash advances updated successfully',
        error: 'Failed to update cash advances',
        loading: 'Updating cash advances...',
      },
      onSuccess: () => api.close(true),
      onError: () => {
        void client.refetchQueries({ include: 'active' });
        api.close(true);
      },
    });

  const handleSubmit = (data: UpdateProgramFormData) => {
    const isCreditCard = data.minimumPaymentStrategy === MinimumPaymentStrategy.CreditCardPercentOfBalance;

    const variables: { programId: string } & UpdateProgramInput = {
      programId,
      name: data.name,
      targetInterestRate: parseFloat(data.targetInterestRate),
      minApr: parseFloat(data.minApr),
      maxApr: parseFloat(data.maxApr),
      firstLateFeeAmount: data.firstLateFeeAmount,
      subsequentLateFeeAmount: data.subsequentLateFeeAmount,
      returnedPaymentFeeCents: data.returnedPaymentFeeCents,
      foreignTransactionFeeRate: percentStringToRate(data.foreignTransactionFeeRatePercent),
      replacementCardFeeCents: data.replacementCardFeeCents,
      lateFeeGracePeriodDays: parseInt(data.lateFeeGracePeriodDays),
      active: programData?.program?.active,
      minimumPaymentStrategy: data.minimumPaymentStrategy,
      minimumPaymentRate: isCreditCard ? percentStringToRate(data.minimumPaymentRatePercent ?? '0') : null,
      minimumPaymentFloorCents: isCreditCard ? (data.minimumPaymentFloorCents ?? 0) : null,
      chargeDrawdownFee: data.chargeDrawdownFee === 'true',
    };

    if (data.pdfTemplate) variables.pdfTemplate = data.pdfTemplate;

    if (data.twispJournalId) variables.twispJournalId = data.twispJournalId;

    if (data.twispIsoConfigId) variables.twispIsoConfigId = data.twispIsoConfigId;

    if (data.twispExposureAccountSetId) variables.twispExposureAccountSetId = data.twispExposureAccountSetId;

    if (hasBillingDefaults) {
      variables.paymentDueDateGracePeriodDays = Number(data.paymentDueDateGracePeriodDays);
      variables.statementGenerateDay = Number(data.statementGenerateDay);
      variables.paymentDueDay = Number(data.paymentDueDay);
    }

    void handleMutation({
      mutation: () => updateProgram({ variables }),
      extractSuccess: (result) => !!result?.data?.updateProgram,
      debugLabel: 'update-program',
      toast: {
        success: 'Program updated successfully',
        error: 'Failed to update program',
        loading: 'Updating program...',
      },
      onSuccess: () =>
        data.cashAdvancesAllowed === currentCashAdvancesAllowed
          ? api.close(true)
          : saveCashAdvances(data.cashAdvancesAllowed),
    });
  };

  const currentStrategy = program?.minimumPaymentStrategy ?? MinimumPaymentStrategy.HelocCumulative;
  const currentLateFeeStrategy = program?.lateFeeStrategy ?? LateFeeStrategy.PercentOfMinimum;
  const currentChargeDrawdownFee = program?.chargeDrawdownFee ? 'true' : 'false';
  const currentRatePercent = decimalRateToPercentString(program?.minimumPaymentRate);
  const currentFloorCents = program?.minimumPaymentFloorCents ?? 0;
  const currentPdfTemplate = program?.pdfTemplate ?? undefined;

  const selectablePdfTemplates =
    currentPdfTemplate && program?.interestPostingPolicy ? PDF_TEMPLATES_BY_POLICY[program.interestPostingPolicy] : [];

  const formConfig: SmartFormConfig<UpdateProgramFormData> = {
    fields: [
      {
        name: 'program_id',
        label: 'Program ID',
        type: 'text',
        required: true,
        defaultValue: program?.id || '',
        placeholder: 'Enter program ID',
      },
      {
        name: 'twispJournalId',
        label: 'Twisp Journal ID (optional)',
        type: 'text',
        defaultValue: program?.twispJournalId ?? '',
        placeholder: 'Leave blank to keep unset',
        helperText: 'Optional Twisp journal UUID used for program-scoped ledger reads.',
      },
      {
        name: 'twispIsoConfigId',
        label: 'Twisp ISO Config ID (optional)',
        type: 'text',
        defaultValue: program?.twispIsoConfigId ?? '',
        placeholder: 'Leave blank to keep unset',
        helperText: 'Optional Twisp ISO config UUID used for program card transaction config.',
      },
      {
        name: 'twispExposureAccountSetId',
        label: 'Twisp Exposure Account Set ID (optional)',
        type: 'text',
        defaultValue: program?.twispExposureAccountSetId ?? '',
        placeholder: 'Leave blank to keep unset',
        helperText: 'Optional Twisp exposure account set UUID used for program exposure rollups.',
      },
      {
        name: 'name',
        label: 'Program Name',
        type: 'text',
        required: true,
        defaultValue: program?.name || '',
        placeholder: 'Enter program name',
      },
      {
        name: 'targetInterestRate',
        label: 'Target Interest Rate (%)',
        type: 'text',
        required: true,
        defaultValue: program?.targetInterestRate || '',
        placeholder: 'e.g., 15.99',
      },
      {
        name: 'minApr',
        label: 'Min APR (%)',
        type: 'text',
        required: true,
        defaultValue: program?.minApr || '',
        placeholder: 'e.g., 9.99',
      },
      {
        name: 'maxApr',
        label: 'Max APR (%)',
        type: 'text',
        required: true,
        defaultValue: program?.maxApr || '',
        placeholder: 'e.g., 24.99',
      },
      {
        name: 'lateFeeStrategy',
        label: 'Late Fee Strategy',
        type: 'select',
        options: lateFeeStrategyOptions,
        defaultValue: currentLateFeeStrategy,
        readOnly: true,
        helperText: 'This disclosed term cannot be changed after program creation.',
      },
      {
        name: 'firstLateFeeAmount',
        label: 'First Late Fee Amount',
        required: true,
        defaultValue: program?.firstLateFeeAmount || 0,
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
        },
      },
      {
        name: 'subsequentLateFeeAmount',
        label: 'Subsequent Late Fee Amount',
        defaultValue: program?.subsequentLateFeeAmount ?? undefined,
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
        },
        helperText: 'Required for flat tiered fees; ignored by percent-of-minimum fees.',
      },
      {
        name: 'paymentDueDateGracePeriodDays',
        readOnly: !hasBillingDefaults,
        label: 'Minimum days from statement close to payment due',
        type: 'text',
        required: true,
        defaultValue: program?.paymentDueDateGracePeriodDays?.toString() || '',
        placeholder: 'Number of days (0-28)',
      },
      {
        name: 'lateFeeGracePeriodDays',
        label: 'Late Fee Grace Period Days',
        type: 'text',
        required: true,
        defaultValue: program?.lateFeeGracePeriodDays?.toString() || '',
        placeholder: 'Number of days (0-28)',
      },
      {
        name: 'statementGenerateDay',
        readOnly: !hasBillingDefaults,
        label: 'Statement generation day',
        type: 'text',
        required: true,
        defaultValue: program?.statementGenerateDay?.toString() || '',
        placeholder: 'Day of month (1-28)',
        helperText: 'Applies to newly boarded loans. Existing loans keep their generation and payment due days.',
      },
      {
        name: 'paymentDueDay',
        readOnly: !hasBillingDefaults,
        label: 'Payment due day',
        type: 'text',
        required: true,
        defaultValue: program?.paymentDueDay?.toString() ?? '',
        placeholder: 'Day of month (1-28)',
      },
      {
        name: 'minimumPaymentStrategy',
        label: 'Minimum Payment Strategy',
        type: 'select',
        required: true,
        options: minimumPaymentStrategyOptions,
        defaultValue: currentStrategy,
      },
      {
        name: 'returnedPaymentFeeCents',
        label: 'Returned Payment Fee',
        required: true,
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
        },
        defaultValue: program?.returnedPaymentFeeCents ?? 0,
        helperText: 'Charged when an ACH repayment is returned by the bank.',
      },
      {
        name: 'returnedPaymentFeeStrategy',
        label: 'Returned Payment Fee Strategy',
        type: 'select',
        options: returnedPaymentFeeStrategyOptions,
        defaultValue: program?.returnedPaymentFeeStrategy ?? undefined,
        readOnly: true,
        helperText: 'This disclosed term cannot be changed after program creation.',
      },
      {
        name: 'foreignTransactionFeeRatePercent',
        label: 'Foreign Transaction Fee Rate (%)',
        type: 'text',
        required: true,
        defaultValue: decimalRateToPercentString(program?.foreignTransactionFeeRate),
        placeholder: 'e.g., 1 for 1%',
        helperText: 'Percentage of the settled amount for transactions in a foreign currency. 0 turns the fee off.',
      },
      {
        name: 'cashAdvancesAllowed',
        label: 'Cash advances allowed',
        type: 'boolean',
        defaultValue: currentCashAdvancesAllowed,
        componentProps: { defaultChecked: currentCashAdvancesAllowed },
        helperText:
          "Cash advances (ATM, cash merchants, cash back) are allowed at the ledger for the program's Lithic cards. Off declines them, and the card statement then prints no Cash Advances section.",
      },
      {
        name: 'cashAdvanceFeeRatePercent',
        label: 'Cash Advance Fee Rate (%)',
        type: 'text',
        defaultValue: decimalRateToPercentString(program?.cashAdvanceFeeRate),
        readOnly: true,
        helperText: 'Not editable here — the update API does not accept cash advance fee changes.',
      },
      {
        name: 'cashAdvanceFeeMinCents',
        label: 'Cash Advance Fee Minimum',
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
          allowZero: true,
        },
        defaultValue: program?.cashAdvanceFeeMinCents ?? 0,
        readOnly: true,
        helperText: 'Not editable here — the update API does not accept cash advance fee changes.',
      },
      {
        name: 'replacementCardFeeCents',
        label: 'Replacement Card Fee',
        required: true,
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
          allowZero: true,
        },
        defaultValue: program?.replacementCardFeeCents ?? 0,
        helperText: "Charged for a card replaced at the cardholder's request. 0 turns the fee off.",
      },
      {
        name: 'graceStrategy',
        label: 'Grace Strategy',
        type: 'select',
        options: graceStrategyOptions,
        defaultValue: program?.graceStrategy ?? undefined,
        readOnly: true,
        helperText: 'This disclosed term cannot be changed after program creation.',
      },
      ...(selectablePdfTemplates.length > 0
        ? [
            {
              name: 'pdfTemplate' as const,
              label: 'Statement PDF Template',
              type: 'select' as const,
              required: true,
              options: toPdfTemplateOptions(selectablePdfTemplates),
              defaultValue: currentPdfTemplate,
              helperText:
                'Applies from the next statement. Statements already issued keep the template they closed with.',
            },
          ]
        : []),
      {
        name: 'chargeDrawdownFee',
        label: 'Charge ACH Drawdown Fee',
        type: 'select',
        required: true,
        options: YES_NO_OPTIONS,
        defaultValue: currentChargeDrawdownFee,
        helperText: 'When enabled, settled ACH drawdowns charge the program credit-line drawdown fee.',
      },
      {
        name: 'minimumPaymentRatePercent',
        label: 'Minimum Payment Rate (%)',
        type: 'text',
        required: true,
        defaultValue: currentRatePercent,
        placeholder: 'e.g., 2 for 2%',
        helperText: 'Percentage of outstanding balance (0–100).',
        conditional: creditCardStrategyCondition,
      },
      {
        name: 'minimumPaymentFloorCents',
        label: 'Minimum Payment Floor',
        required: true,
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
        },
        defaultValue: currentFloorCents,
        helperText: 'Floor applied when percent-of-balance is below this amount.',
        conditional: creditCardStrategyCondition,
      },
    ],
    schema: updateProgramSchema(hasBillingDefaults),
    defaultValues: {
      program_id: program?.id || '',
      twispJournalId: program?.twispJournalId ?? '',
      twispIsoConfigId: program?.twispIsoConfigId ?? '',
      twispExposureAccountSetId: program?.twispExposureAccountSetId ?? '',
      name: program?.name || '',
      targetInterestRate: program?.targetInterestRate || '',
      minApr: program?.minApr || '',
      maxApr: program?.maxApr || '',
      lateFeeStrategy: currentLateFeeStrategy,
      firstLateFeeAmount: program?.firstLateFeeAmount || 0,
      subsequentLateFeeAmount: program?.subsequentLateFeeAmount ?? undefined,
      paymentDueDateGracePeriodDays: program?.paymentDueDateGracePeriodDays?.toString() || '',
      lateFeeGracePeriodDays: program?.lateFeeGracePeriodDays?.toString() || '',
      statementGenerateDay: program?.statementGenerateDay?.toString() || '',
      paymentDueDay: program?.paymentDueDay?.toString() ?? '',
      minimumPaymentStrategy: currentStrategy,
      graceStrategy: program?.graceStrategy ?? undefined,
      pdfTemplate: currentPdfTemplate,
      returnedPaymentFeeCents: program?.returnedPaymentFeeCents ?? 0,
      returnedPaymentFeeStrategy: program?.returnedPaymentFeeStrategy ?? undefined,
      foreignTransactionFeeRatePercent: decimalRateToPercentString(program?.foreignTransactionFeeRate),
      cashAdvancesAllowed: currentCashAdvancesAllowed,
      cashAdvanceFeeRatePercent: decimalRateToPercentString(program?.cashAdvanceFeeRate),
      cashAdvanceFeeMinCents: program?.cashAdvanceFeeMinCents ?? 0,
      replacementCardFeeCents: program?.replacementCardFeeCents ?? 0,
      chargeDrawdownFee: currentChargeDrawdownFee,
      minimumPaymentRatePercent: currentRatePercent,
      minimumPaymentFloorCents: currentFloorCents,
    },
  };

  if (!program) {
    return <div>Loading program data...</div>;
  }

  return createFormComponent({
    config: formConfig,
    handleSubmit,
    loading: loading || policyLoading,
  });
};
